import { ListPage } from '@components/molecules';
import { DatePickerElement } from 'react-hook-form-mui/date-pickers';
import {
  Box,
  Button,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import API from '@apps/nnc/services/nnc';
import { ModelProps } from '@/types';
import { useTranslation } from 'react-i18next';
import { JSX, useEffect, useState } from 'react';
import { NncSchema, NncSearchSchema } from '@apps/nnc/schemas/nnc';
import AsyncAutocompleteElement from '@/components/atoms/asyncautocomplete';
import CountryAPI from '@apps/localization/services/country';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import UnitAPI from '@apps/localization/services/unit';
// import { AutocompleteElement } from 'react-hook-form-mui';
import { GridRenderCellParams, GridRowSelectionModel } from '@mui/x-data-grid';
import { useNavigate } from 'react-router';
import { useMenuContext } from '@/contexts/menu';
import { YesNoAutocompleteElement } from '@/components/atoms';
import DivisionAPI from '@apps/localization/services/division';

export default function EntriesListPage(modelProps: ModelProps): JSX.Element {
  const { t } = useTranslation();

  const defaultValues = {
    unfounded: { id: 'false', label: t('No') },
  };

  const clearValues = {
    unfounded: null,
  };

  const defaultFormContext = useForm<z.infer<z.ZodTypeAny>>({
    defaultValues: defaultValues,
  });
  const start = defaultFormContext.watch('start_date', null);
  const end = defaultFormContext.watch('end_date', null);
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  const [openModal, setOpenModal] = useState(false);
  const [justification, setJustification] = useState('');
  const [formEffect, setFormEffect] = useState(false);
  const { openMenu } = useMenuContext();
  const navigate = useNavigate();
  const [refetch, setRefresh] = useState(false);
  const [rows, setRows] = useState<
    Array<{ id: string | number; unit?: { id: number }; unfounded?: boolean }>
  >([]);
  const [selectedUnitId, setSelectedUnitId] = useState<number | null>(null);

  const handleCreate = () => {
    navigate(`/nnc/entries/add-update/${selection.join('-')}`);
  };

  useEffect(() => {
    if (selection.length === 0) {
      // Limpa selectedUnitId quando não há seleção
      setSelectedUnitId(null);
    } else if (selection.length === 1 && selectedUnitId === null) {
      // Primeira seleção - armazena a unit.id
      const firstSelection = rows.find((row) => row.id === selection[0]);
      if (firstSelection && firstSelection.unit) {
        setSelectedUnitId(firstSelection.unit.id);
      }
    }
  }, [selection, rows, selectedUnitId]);

  const columns = [
    {
      field: 'reference_number',
      headerName: t('ID'),
      width: 250,
    },
    {
      field: 'nnc_date',
      headerName: t('Date'),
      width: 100,
    },
    {
      field: 'unit',
      headerName: t('Unit'),
      renderCell: (params: GridRenderCellParams) => {
        return params?.row?.unit?.label;
      },
      flex: 1,
    },
    {
      field: 'division_name',
      headerName: t('Identified Area'),
      flex: 1,
    },
    {
      field: 'production_phase',
      headerName: t('Production Phase'),
      renderCell: (params: GridRenderCellParams) => {
        return params?.row?.production_phase?.label;
      },
      flex: 1,
      sortable: false,
    },
    {
      field: 'machine',
      headerName: t('Machine'),
      flex: 1,
    },
    {
      field: 'machine_enrichment',
      headerName: t('NNC Machine'),
      renderCell: (params: GridRenderCellParams) => {
        return params?.row?.machine_enrichment?.label;
      },
      flex: 1,
    },
    {
      field: 'operator_name_enrichment',
      headerName: t('Operator Generator'),
      renderCell: (params: GridRenderCellParams) => {
        return params?.row?.operator_name_enrichment?.label;
      },
      flex: 1,
    },
    {
      field: 'comment',
      headerName: t('NNC Comment'),
      flex: 2,
    },
    {
      field: 'status',
      headerName: t('Status'),
      renderCell: (params: GridRenderCellParams) => {
        if (params?.row?.unfounded) {
          return t('Unfounded');
        }
        return params?.row?.status?.label;
      },
      width: 150,
    },
  ];

  useEffect(() => {
    if (!start && !end) {
      setRefresh(!refetch);
    }
  }, [start, end]);

  return (
    <>
      <Box sx={{ position: 'relative', pb: 12 }}>
        <ListPage
          formcontext={defaultFormContext}
          schema={NncSchema}
          schemaSearch={NncSearchSchema}
          title={t('NNC Entries')}
          customPath={'nnc/entries'}
          api={API}
          columns={columns}
          modelProps={modelProps}
          resetDefaultValues={clearValues}
          transformQueryParams={(params) => ({
            ...params,
            status: 3,
          })}
          checkboxSelection
          rowSelectionModel={selection}
          setRowSelectionModel={setSelection}
          disableAdd
          disableDelete
          disableEdit
          effect={formEffect}
          isRowSelectable={(params) => {
            // Se é unfounded, não pode ser selecionado
            if (params.row.unfounded) {
              return false;
            }

            // Se não há seleção, qualquer item pode ser selecionado
            if (selection.length === 0) {
              return true;
            }

            // Se já há seleção, só permite itens da mesma unidade
            if (selectedUnitId !== null) {
              return params.row.unit?.id === selectedUnitId;
            }

            return true;
          }}
          apiReturn={setRows}
        >
          <Grid size={{ xs: 12, md: 2.8 }}>
            <DatePickerElement
              name="start_date"
              label={t('Start Date')}
              inputProps={{ sx: { width: '100%' } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2.8 }}>
            <DatePickerElement
              name="end_date"
              label={t('End Date')}
              inputProps={{ sx: { width: '100%' } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2.8 }}>
            <AsyncAutocompleteElement
              api={() =>
                DivisionAPI.select({
                  is_active: 1,
                })
              }
              name="division"
              label={t('Identified Area')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2.8 }}>
            <AsyncAutocompleteElement
              api={CountryAPI.select}
              name="country"
              label={t('Country')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2.8 }}>
            <AsyncAutocompleteElement
              api={UnitAPI.select}
              name="unit"
              label={t('Unit')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2.8 }}>
            <YesNoAutocompleteElement name="unfounded" label={t('Unfounded')} />
          </Grid>
          {/* <Grid size={{ xs: 12, md: 2.8 }}>
            <TextFieldElement
              name="responsible"
              label={t('Responsible')}
              fullWidth
            />
          </Grid> */}
          {/* <Grid size={{ xs: 12, md: 5 }}>
        <AutocompleteElement
          name="status"
          label={t('Status')}
          multiple
          options={opt}
        />
      </Grid> */}

          {selection.length > 0 ? (
            <Box
              sx={{
                position: 'fixed',
                bottom: 30,
                width: openMenu
                  ? 'calc(100vw - 240px - 48px)'
                  : 'calc(100vw - 48px)',
                padding: 2,
                background: '#FFF',
                zIndex: 1,
                boxShadow: '4px 4px 4px #00000050',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                marginLeft: '-16px',
              }}
            >
              <Typography>{t('NNC Evaluation')}</Typography>
              <Box display={'flex'} gap={2}>
                <Button
                  onClick={() => setOpenModal(true)}
                  sx={{ background: '#191B27' }}
                >
                  {t('Dismiss NNC')}
                </Button>
                <Button onClick={() => handleCreate()}>
                  {t('Create Analysis')}
                </Button>
              </Box>
            </Box>
          ) : (
            <></>
          )}
        </ListPage>
      </Box>

      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        slotProps={{
          paper: { sx: { borderRadius: '16px !important', maxWidth: '700px' } },
        }}
      >
        <DialogTitle
          justifyContent={'space-between'}
          display="flex"
          alignItems="center"
        >
          {t('Justification')}
          <Button
            color="inherit"
            variant="text"
            onClick={() => setOpenModal(false)}
          >
            X
          </Button>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('Describe Justification')}
            type="text"
            fullWidth
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            sx={{ width: '600px' }}
            multiline
            rows={4}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              await API.unfounded(selection, justification);
              setOpenModal(false);
              setFormEffect(!formEffect);
              setJustification('');
            }}
            disabled={!justification}
            variant="contained"
            color="primary"
          >
            {t('Save and Close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
